import { textStyles } from "../../styles/typography";
import { ccn, cn } from "../../styles/utils";
import BookingSummary from "./BookingSummary";
import Button from "../ui/Button";
import { CheckIcon } from "../ui/icons";
import { useAtomValue } from "jotai";
import { customerDetailsAtom, paymentResponseAtom } from "./store";
import { useFormatters } from "../../contexts/formatters";
import { useListingQuery } from "../../api/hooks";
import { getImageUrl } from "../../utils/image";
import { closeWidget } from "../../utils/controls";

type Props = {
  heading?: string;
  body?: string;
  summaryTitle?: string;
  showPayment?: boolean;
};

export default function PaymentConfirmation({
  heading,
  body = "A confirmation letter and all the details about the booking will be sent to your email.",
  summaryTitle,
  showPayment = true,
}: Props = {}) {
  const customerDetails = useAtomValue(customerDetailsAtom);
  // const selectedDay = useAtomValue(selectedDateAtom);
  // const selectedSlot = useAtomValue(selectedSlotAtom);
  const paymentResponse = useAtomValue(paymentResponseAtom);
  const transactionDate = paymentResponse?.paidOn;
  const { dateFormatter } = useFormatters();

  const { data: listing } = useListingQuery();

  const fullName = customerDetails?.name;

  return (
    <div className="widget-confirmation px-3">
      <div className="widget-confirmation__banner bg-green-primary/15 text-green-primary rounded-lg flex items-center md:flex-row flex-col-reverse mb-5 p-3 md:p-10 gap-7">
        {listing?.boat?.boatPicture && (
          <img
            src={getImageUrl(listing?.boat?.boatPicture, {
              height: 180,
              width: 320,
            })}
            alt={listing.title ?? ""}
            className="widget-confirmation__boat-image md:mx-auto h-44 md:h-32 object-cover rounded-xl w-full md:w-1/4"
          />
        )}

        <div className="widget-confirmation__banner-content">
          <h1
            className={ccn(
              textStyles.h1,
              "widget-confirmation__heading md:mb-3 mb-3 flex md:flex-row flex-col gap-2",
            )}
          >
            <CheckIcon className="w-8 h-8" />
            {heading ?? `Hello ${fullName}, your booking is reserved`}
          </h1>

          <div
            className={cn(
              textStyles.body1,
              "widget-confirmation__subtext md:max-w-screen-sm",
            )}
          >
            {body}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-1">
        {/* Trip details */}
        <div className="widget-confirmation__details bg-grey-light md:p-10 p-3 rounded-xl mb-10">
          {/* <div className="mb-10"> */}
          {/* <div className={cn("uppercase text-center mt-7", textStyles.sub1)}>
              <div className="text-blue-dark">{boat?.Label}</div>
              <div className="text-blue-light">
                {formatDate(selectedDay)}
                {selectedSlot && (
                  <>
                    ; {formatSlotTime(selectedSlot?.interval.start)} —{" "}
                    {formatSlotTime(selectedSlot?.interval.end)}
                  </>
                )}
              </div>
            </div> */}
          {/* </div> */}

          <BookingSummary title={summaryTitle} />
          {showPayment && (
            <>
              <div
                className={cn(
                  textStyles.sub1,
                  "widget-confirmation__section-title text-blue-dark mb-4 mt-6",
                )}
              >
                Payment Details
              </div>
              <ul className="widget-confirmation__payment-list text-blue-dark flex flex-col gap-3">
                <li className="widget-confirmation__detail-row flex items-center justify-between">
                  <div className="widget-confirmation__detail-label text-blue-dark60">
                    Card
                  </div>{" "}
                  <div className="widget-confirmation__detail-value">
                    *{paymentResponse?.cardLast4}
                  </div>
                </li>
                <li className="widget-confirmation__detail-row flex items-center justify-between">
                  <div className="widget-confirmation__detail-label text-blue-dark60">
                    Date
                  </div>{" "}
                  {transactionDate && (
                    <div className="widget-confirmation__detail-value">
                      {dateFormatter.formatDateOnly(transactionDate)};{" "}
                      {dateFormatter.formatTimeOnly(transactionDate)}
                    </div>
                  )}
                </li>
              </ul>
            </>
          )}

          <div className="">
            <div
              className={cn(
                textStyles.sub1,
                "widget-confirmation__section-title text-blue-dark mb-4 mt-7",
              )}
            >
              Contact details
            </div>
            <ul className="widget-confirmation__contact-list flex flex-col gap-3 mb-3">
              <li className="widget-confirmation__detail-row flex justify-between">
                <div
                  className={cn(
                    textStyles.body1,
                    "widget-confirmation__detail-label text-blue-dark60",
                  )}
                >
                  Full Name
                </div>
                <div
                  className={cn(
                    textStyles.sub1,
                    "widget-confirmation__detail-value text-blue-dark",
                  )}
                >
                  {fullName}
                </div>
              </li>
              <li className="widget-confirmation__detail-row flex justify-between">
                <div
                  className={cn(
                    textStyles.body1,
                    "widget-confirmation__detail-label text-blue-dark60",
                  )}
                >
                  Phone
                </div>
                <div
                  className={cn(
                    textStyles.sub1,
                    "widget-confirmation__detail-value text-blue-dark",
                  )}
                >
                  {customerDetails?.phone}
                </div>
              </li>
              <li className="widget-confirmation__detail-row flex justify-between">
                <div
                  className={cn(
                    textStyles.body1,
                    "widget-confirmation__detail-label text-blue-dark60",
                  )}
                >
                  E-mail
                </div>
                <div
                  className={cn(
                    textStyles.sub1,
                    "widget-confirmation__detail-value text-blue-dark",
                  )}
                >
                  {customerDetails?.email}
                </div>
              </li>
              <li className="widget-confirmation__detail-row flex justify-between">
                <div
                  className={cn(
                    textStyles.body1,
                    "widget-confirmation__detail-label text-blue-dark60",
                  )}
                >
                  Message
                </div>
                <div
                  className={cn(
                    textStyles.sub1,
                    "widget-confirmation__detail-value text-blue-dark",
                  )}
                >
                  {customerDetails?.message ?? "-"}
                </div>
              </li>
            </ul>
          </div>

          {/* {boat.Agent && (
            <div className="flex items-center flex-col mt-10">
              <div
                className={ccn(
                  "text-green-primary text-center mb-3",
                  textStyles.h1
                )}
              >
                Need Assistance? Contact your Agent
              </div>
              <div className="flex justify-center items-center gap-3">
                <img
                  src={boat.Picture.Url}
                  alt={boat.Label}
                  className="h-32 object-cover rounded-xl w-1/4"
                />
                <div
                  className={ccn(
                    "flex flex-col gap-2 text-blue-dark",
                    textStyles.sub1
                  )}
                >
                  <div>{boat.Agent.Name}</div>
                  <div>{boat.Agent.Phone}</div>
                  <a href={`mailto:${boat.Agent.Email}`}>{boat.Agent.Email}</a>
                </div>
              </div>
            </div>
          )} */}

          <Button
            type="button"
            variant="primary"
            className="widget-confirmation__close mt-10 w-full"
            onClick={closeWidget}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
